'use strict';

import ready from '../../../js/lib/ready';
import CanvasOverlay from '../../../js/lib/CanvasOverlay';
import CanvasImage from '../../../js/lib/CanvasImage';
import vsSource from '../glsl/vertex-shader-2d.glsl';
import fsSource from '../glsl/fragment-shader-2d.glsl';
import * as twgl from 'twgl.js';
import promiseIdle from '../../../js/lib/promise-idle';
import debounce from '../../../js/lib/debounce';
import fd from '../../../js/lib/promise-fastdom';
import easing from 'bezier-easing';

function ColorEffect(image) {

	const self = this;

	const uniforms = {};
	let texture;

	this.offsetX = 0;
	this.offsetY = 0;
	this.initialized = false;

	// color correction
	this.cc = [-1.0, 1.0, -1.0, 1.0];

	this.overlay = new CanvasOverlay(image);

	this.cropCanvas = new CanvasImage(image, {insert: false});
	this.cropCanvas.addObserver(() => {
		texture = twgl.createTexture(this.gl, {
			wrap: this.gl.CLAMP_TO_EDGE,
			minMag: this.gl.LINEAR,
			src: this.cropCanvas.canvas,
		});
		this.initialized = true;
		drawScene();
	});

	this.gl = this.overlay.canvas.getContext('webgl');
	this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
	this.gl.enable(this.gl.BLEND);

	// noinspection JSCheckFunctionSignatures
	this.programInfo = twgl.createProgramInfo(this.gl, [vsSource, fsSource]);

	const bufferInfo = twgl.createBufferInfoFromArrays(self.gl, {
		a_position: [
			-1.0, +1.0, 0,
			+1.0, +1.0, 0,
			-1.0, -1.0, 0,
			+1.0, +1.0, 0,
			+1.0, -1.0, 0,
			-1.0, -1.0, 0,
		],
		a_texCoord: [
			0.0, 0.0,
			1.0, 0.0,
			0.0, 1.0,
			1.0, 0.0,
			1.0, 1.0,
			0.0, 1.0
		],
	});

	function drawScene() {
		twgl.resizeCanvasToDisplaySize(self.gl.canvas);

		self.gl.viewport(0, 0, self.gl.drawingBufferWidth, self.gl.drawingBufferHeight);
		self.gl.clear(self.gl.COLOR_BUFFER_BIT);
		self.gl.useProgram(self.programInfo.program);

		uniforms.u_xoffset = self.offsetX / self.gl.canvas.width;
		uniforms.u_yoffset = self.offsetY / self.gl.canvas.height;
		uniforms.u_texture = texture;
		uniforms.v_cc = self.cc;

		twgl.setBuffersAndAttributes(self.gl, self.programInfo, bufferInfo);
		twgl.setUniforms(self.programInfo, uniforms);
		twgl.drawBufferInfo(self.gl, bufferInfo);
	}

	this.drawScene = drawScene;
}

ColorEffect.prototype.offset = function (x, y) {
	this.offsetX = x;
	this.offsetY = y;
	this.initialized && this.drawScene();
};

ColorEffect.prototype.render = function () {
	this.initialized && this.drawScene();
};

ColorEffect.prototype.destroy = function () {
	this.cropCanvas.destroy();
	this.overlay.destroy();
};


const selector = 'img[data-color-effect]';
const items = new Map();

ready().then(promiseIdle()).then(function () {

	// disable in prefers-reduced-motion
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return;
	}

	window.addEventListener('resize', debounce(function () {
		return fd.measure(function () {
			items.forEach(function (item, image) {
				if (!document.body.contains(image)) {
					return;
				}
				Object.assign(item, elementRect(image));
			});
		});
	}));

	reload();
	document.addEventListener('reload', reload);

	if (window.matchMedia('(pointer:fine)').matches) {
		const max = 20;
		const ease = easing(.5, .5, .5, 1);
		const mouseHandler = function (e) {
			const norm = Math.min(window.innerWidth, window.innerHeight);
			items.forEach(function (item) {
				const x = ((item.x + item.w * .5 - window.scrollX) - e.x) / norm * 2;
				const y = ((item.y + item.h * .5 - window.scrollY) - e.y) / norm * 2;
				fieldset.style.setProperty('--angle', `${Math.atan2(y, x) * 180 / Math.PI + 180}deg`);
				item.effect.offset(
					// x * max, y * max
					ease(Math.min(Math.abs(x), 1)) * Math.sign(x) * max,
					ease(Math.min(Math.abs(y), 1)) * Math.sign(y) * max,
				);
			});
		};
		document.addEventListener('mousemove', mouseHandler);
	} else {
		const scrollHandler = debounce(function () {
			items.forEach(function (item) {
				const offset = window.innerHeight * .5 - (item.y + item.h * .75 - window.scrollY);
				item.effect.offset(0, offset * (40 / window.innerHeight));
			});
		}, 16);
		scrollHandler();
		window.addEventListener('scroll', scrollHandler, {passive: true});
	}

	// correction controls
	const controls = {
		none: {
			label: 'Sans correction',
			colors: ['#f00', '#0ff'],
			values: [-1, 1.0, -1, 1.0],
		},
		drop: {
			label: 'Suppression de la frange bleu-vert',
			colors: ['#f00', '#999'],
			values: [-1, 1.0, -1, 0.0],
		},
		blue: {
			label: 'Bleu / Violet',
			colors: ['#005F8C', '#7A5195'],
			values: [.5527, .74, .7666, .35],
		},
		pink: {
			label: 'Rose / Jaune',
			colors: ['#EF5675', '#FFA600'],
			values: [.9666, .8, .1083, 1.0],
		},
		green: {
			label: 'Jaune / Vert',
			colors: ['#5DB996', '#E3F0AF'],
			values: [.4361, .5, .2, .27],
		},
	};

	const form = document.createElement('form');
	const fieldset = document.createElement('fieldset');
	const legend = document.createElement('legend');
	legend.innerHTML = 'Correction des couleurs';
	fieldset.appendChild(legend);
	form.appendChild(fieldset);
	Object.entries(controls).forEach(([id, value]) => {
		fieldset.insertAdjacentHTML('beforeend', `
		<input type="radio" name="correction" id="${id}" value="${id}"${id === 'none' ? ' checked' : ''}>
		<label for="${id}" style="--cl:${value.colors[0]};--cr:${value.colors[1]}" title="${value.label}">
		<span></span>
		</label>
		`);
	});
	document.getElementById('color-correction').appendChild(form);
	form.addEventListener('change', update);

	update();

	function update() {
		items.forEach(function (item) {
			item.effect.cc = controls[form['correction'].value].values;
			item.effect.render();
		});
	}
});

function reload() {
	// clear removed images
	items.forEach(function (item, image) {
		if (!document.body.contains(image)) {
			item.destroy();
			items.delete(image);
		}
	});

	// init added images
	document.querySelectorAll(selector).forEach(function (image) {
		if (!items.has(image)) {
			items.set(image, {
				...elementRect(image),
				effect: new ColorEffect(image)
			});
		}
	});
}

function elementRect(node) {
	const rect = node.getBoundingClientRect();
	return {
		x: rect.left + window.scrollX,
		y: rect.top + window.scrollY,
		w: rect.width,
		h: rect.height,
	};
}
